import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { quotesApi, invoicesApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel, downloadBlob } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
    FileText, 
    Receipt, 
    Search, 
    Download, 
    Edit2,
    Trash2,
    ArrowRight,
    CalendarIcon,
    Plus,
    CheckCircle
} from 'lucide-react';

export default function DocumentsPage() {
    const [quotes, setQuotes] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('quotes');
    
    // Invoice conversion dialog
    const [convertDialog, setConvertDialog] = useState({ open: false, quote: null });
    const [invoiceDate, setInvoiceDate] = useState(new Date());

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [quotesRes, invoicesRes] = await Promise.all([
                quotesApi.getAll(),
                invoicesApi.getAll(),
            ]);
            setQuotes(quotesRes.data);
            setInvoices(invoicesRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadQuotePdf = async (quote) => {
        try {
            const response = await quotesApi.getPdf(quote.id);
            downloadBlob(response.data, `devis-${quote.quote_number}.pdf`);
            toast.success('PDF téléchargé');
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleDownloadInvoicePdf = async (invoice) => {
        try {
            const response = await invoicesApi.getPdf(invoice.id);
            downloadBlob(response.data, `facture-${invoice.invoice_number}.pdf`);
            toast.success('PDF téléchargé');
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleDeleteQuote = async (quoteId) => {
        if (!window.confirm('Supprimer ce devis définitivement ?')) return;
        try {
            await quotesApi.delete(quoteId);
            toast.success('Devis supprimé');
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleDeleteInvoice = async (invoiceId) => {
        if (!window.confirm('Supprimer cette facture définitivement ?')) return;
        try {
            await invoicesApi.delete(invoiceId);
            toast.success('Facture supprimée');
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleConvertToInvoice = async () => {
        if (!convertDialog.quote) return;
        
        try {
            await invoicesApi.create({
                quote_id: convertDialog.quote.id,
                invoice_date: invoiceDate.toISOString().split('T')[0],
            });
            toast.success('Facture créée');
            setConvertDialog({ open: false, quote: null });
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la conversion');
        }
    };

    const handleMarkPaid = async (invoiceId) => {
        try {
            await invoicesApi.updateStatus(invoiceId, 'paid');
            toast.success('Facture marquée comme payée');
            loadData();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const filteredQuotes = quotes.filter(q => 
        q.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.quote_number.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredInvoices = invoices.filter(inv => 
        inv.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="p-8">
                <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="stat-card h-20 skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="documents-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#f8fafc]">Documents</h1>
                    <p className="text-[#94a3b8] text-sm sm:text-base mt-1">
                        {quotes.length} devis · {invoices.length} factures
                    </p>
                </div>
                <Link to="/quotes/new" className="w-full sm:w-auto">
                    <Button className="bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold w-full sm:w-auto">
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Nouveau devis
                    </Button>
                </Link>
            </div>

            {/* Tabs & Search */}
            <div className="flex flex-col gap-3 sm:gap-4">
                {/* Tabs - scrollable on mobile */}
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    <button
                        onClick={() => setActiveTab('quotes')}
                        className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                            activeTab === 'quotes'
                                ? 'bg-[#d4af37]/20 text-[#d4af37]'
                                : 'text-[#94a3b8] hover:bg-[#1e293b]'
                        }`}
                        data-testid="tab-quotes"
                    >
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                        Devis ({quotes.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('invoices')}
                        className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                            activeTab === 'invoices'
                                ? 'bg-[#d4af37]/20 text-[#d4af37]'
                                : 'text-[#94a3b8] hover:bg-[#1e293b]'
                        }`}
                        data-testid="tab-invoices"
                    >
                        <Receipt className="w-4 h-4 sm:w-5 sm:h-5" />
                        Factures ({invoices.length})
                    </button>
                </div>
                {/* Search */}
                <div className="relative w-full sm:max-w-md sm:ml-auto">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-[#475569]" />
                    <Input
                        data-testid="search-documents-input"
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 sm:pl-10 bg-[#0f172a] border-[#1e293b] text-[#f8fafc] text-sm sm:text-base"
                    />
                </div>
            </div>

            {/* Quotes List */}
            {activeTab === 'quotes' && (
                <div className="space-y-2 sm:space-y-3">
                    {filteredQuotes.length > 0 ? (
                        filteredQuotes.map((quote, index) => (
                            <div
                                key={quote.id}
                                className="document-row p-3 sm:p-4 animate-fadeInUp"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                data-testid={`quote-row-${quote.id}`}
                            >
                                {/* Mobile: Stack layout */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                    {/* Info */}
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-blue-400/10 flex items-center justify-center shrink-0">
                                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                <p className="text-[#f8fafc] font-medium text-sm sm:text-base truncate">{quote.client_name}</p>
                                                <span className={`status-badge status-${quote.status} text-xs`}>
                                                    {getStatusLabel(quote.status)}
                                                </span>
                                            </div>
                                            <p className="text-[#64748b] text-xs sm:text-sm mono">{quote.quote_number}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Price & Actions */}
                                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-12 sm:pl-0">
                                        <div className="text-left sm:text-right">
                                            <p className="text-[#d4af37] font-bold mono text-sm sm:text-base">{formatCurrency(quote.total_sale)}</p>
                                            <p className="text-[#64748b] text-xs">{formatDate(quote.created_at)}</p>
                                        </div>
                                        <div className="flex gap-1 sm:gap-2">
                                            {quote.status !== 'converted' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setInvoiceDate(new Date());
                                                        setConvertDialog({ open: true, quote });
                                                    }}
                                                    className="text-green-400 hover:text-green-300 hover:bg-green-400/10 p-1.5 sm:p-2"
                                                    title="Convertir en facture"
                                                    data-testid={`convert-quote-${quote.id}`}
                                                >
                                                    <ArrowRight className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownloadQuotePdf(quote)}
                                                className="text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b] p-1.5 sm:p-2"
                                                data-testid={`download-quote-${quote.id}`}
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                            <Link to={`/quotes/${quote.id}/edit`} className="hidden sm:block">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b] p-2"
                                                    data-testid={`edit-quote-${quote.id}`}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                            </Link>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteQuote(quote.id)}
                                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10 p-1.5 sm:p-2"
                                                data-testid={`delete-quote-${quote.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-10 sm:py-16">
                            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-[#334155] mx-auto mb-3 sm:mb-4" />
                            <h3 className="text-[#f8fafc] text-lg sm:text-xl font-semibold mb-2">Aucun devis</h3>
                            <p className="text-[#64748b] text-sm sm:text-base mb-4">
                                {searchQuery ? 'Aucun résultat' : 'Créez votre premier devis'}
                            </p>
                            {!searchQuery && (
                                <Link to="/quotes/new">
                                    <Button className="bg-[#d4af37] text-[#020617]">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouveau devis
                                    </Button>
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Invoices List */}
            {activeTab === 'invoices' && (
                <div className="space-y-2 sm:space-y-3">
                    {filteredInvoices.length > 0 ? (
                        filteredInvoices.map((invoice, index) => (
                            <div
                                key={invoice.id}
                                className="document-row p-3 sm:p-4 animate-fadeInUp"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                data-testid={`invoice-row-${invoice.id}`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-green-400/10 flex items-center justify-center shrink-0">
                                            <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                <p className="text-[#f8fafc] font-medium text-sm sm:text-base truncate">{invoice.client_name}</p>
                                                <span className={`status-badge status-${invoice.status} text-xs`}>
                                                    {getStatusLabel(invoice.status)}
                                                </span>
                                            </div>
                                            <p className="text-[#64748b] text-xs sm:text-sm mono">{invoice.invoice_number}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-12 sm:pl-0">
                                        <div className="text-left sm:text-right">
                                            <p className="text-[#d4af37] font-bold mono text-sm sm:text-base">{formatCurrency(invoice.total_sale)}</p>
                                            <p className="text-[#64748b] text-xs">{formatDate(invoice.invoice_date)}</p>
                                        </div>
                                        <div className="flex gap-1 sm:gap-2">
                                            {invoice.status !== 'paid' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleMarkPaid(invoice.id)}
                                                    className="text-green-400 hover:text-green-300 hover:bg-green-400/10 p-1.5 sm:p-2"
                                                    title="Marquer comme payée"
                                                    data-testid={`mark-paid-${invoice.id}`}
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownloadInvoicePdf(invoice)}
                                                className="text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#1e293b] p-1.5 sm:p-2"
                                                data-testid={`download-invoice-${invoice.id}`}
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteInvoice(invoice.id)}
                                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10 p-1.5 sm:p-2"
                                                data-testid={`delete-invoice-${invoice.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-16">
                            <Receipt className="w-16 h-16 text-[#334155] mx-auto mb-4" />
                            <h3 className="text-[#f8fafc] text-xl font-semibold mb-2">Aucune facture</h3>
                            <p className="text-[#64748b]">
                                {searchQuery ? 'Aucun résultat' : 'Convertissez un devis en facture'}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Convert to Invoice Dialog */}
            <Dialog open={convertDialog.open} onOpenChange={(open) => setConvertDialog({ ...convertDialog, open })}>
                <DialogContent className="bg-[#0f172a] border-[#1e293b] text-[#f8fafc] max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">Convertir en facture</DialogTitle>
                    </DialogHeader>
                    
                    {convertDialog.quote && (
                        <div className="space-y-4 mt-4">
                            <div className="p-4 rounded-lg bg-[#020617]/50 border border-[#1e293b]">
                                <p className="text-[#94a3b8] text-sm">Devis</p>
                                <p className="text-[#f8fafc] font-medium">{convertDialog.quote.quote_number}</p>
                                <p className="text-[#64748b] text-sm">{convertDialog.quote.client_name}</p>
                                <p className="text-[#d4af37] font-bold mt-2 mono">
                                    {formatCurrency(convertDialog.quote.total_sale)}
                                </p>
                            </div>

                            <div>
                                <p className="text-[#94a3b8] text-sm mb-2">Date de facturation</p>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            data-testid="invoice-date-btn"
                                            className="w-full justify-start text-left font-normal bg-[#020617] border-[#1e293b] text-[#f8fafc] hover:bg-[#1e293b]"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {format(invoiceDate, 'PPP', { locale: fr })}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 bg-[#0f172a] border-[#1e293b]" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={invoiceDate}
                                            onSelect={(date) => date && setInvoiceDate(date)}
                                            locale={fr}
                                            className="bg-[#0f172a] text-[#f8fafc]"
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setConvertDialog({ open: false, quote: null })}
                                    className="flex-1 border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    onClick={handleConvertToInvoice}
                                    data-testid="confirm-convert-btn"
                                    className="flex-1 bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d]"
                                >
                                    <Receipt className="w-4 h-4 mr-2" />
                                    Créer la facture
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
